import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMe, useSearch, useTopics } from '../api/hooks';
import { ErrorPanel, Loading } from '../components/ErrorPanel';
import { Icon } from '../components/Icon';
import { ResultList } from '../components/ResultList';
import { SearchBox } from '../components/SearchBox';
import { prefs, writePref } from '../storage';
import { strings } from '../strings';
import { tenantDisplayName } from '../tenants';

export function Home() {
  const { tenant = '' } = useParams();
  const navigate = useNavigate();
  const me = useMe();
  const recent = useSearch(tenant, { sort: 'updated_desc', pageSize: 5 });
  const topics = useTopics(tenant);
  const base = `/t/${encodeURIComponent(tenant)}`;

  useEffect(() => writePref(prefs.tenant, tenant), [tenant]);

  const current = me.data?.tenants.find((t) => t.tenantName === tenant);
  const title = current ? tenantDisplayName(current) : tenant;

  return (
    <div className="space-y-10">
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <h1 className="m-0">{title}</h1>
        {(me.data?.tenants.length ?? 0) > 1 && <Link to="/?choose">{strings.header.switchPilot}</Link>}
      </div>

      <section aria-labelledby="search-title" className="card">
        <h2 id="search-title" className="mt-0">
          {strings.home.searchTitle}
        </h2>
        <SearchBox onSearch={(q) => navigate(`${base}/search?q=${encodeURIComponent(q)}`)} />
        <p className="mb-0 mt-3">
          <Link to={`${base}/search`}>{strings.home.viewAll}</Link>
        </p>
      </section>

      <section aria-labelledby="recent-title">
        <h2 id="recent-title">{strings.home.recent}</h2>
        {recent.isPending ? (
          <Loading />
        ) : recent.isError ? (
          <ErrorPanel error={recent.error} onRetry={() => void recent.refetch()} />
        ) : recent.data.items.length === 0 ? (
          <p>{strings.search.none}</p>
        ) : (
          <ResultList tenant={tenant} items={recent.data.items} />
        )}
      </section>

      <section aria-labelledby="topics-title">
        <h2 id="topics-title">{strings.home.topics}</h2>
        {topics.isPending ? (
          <Loading />
        ) : topics.isError ? (
          <ErrorPanel error={topics.error} onRetry={() => void topics.refetch()} />
        ) : topics.data.items.length === 0 ? (
          <p>{strings.home.noTopics}</p>
        ) : (
          <ul className="m-0 grid list-none gap-4 p-0 sm:grid-cols-2 lg:grid-cols-3">
            {topics.data.items.map((topic) => (
              <li key={topic.id}>
                <Link
                  to={`${base}/topics/${encodeURIComponent(topic.id)}`}
                  className="card flex h-full flex-col gap-2 text-brand-prussian no-underline hover:border-brand-orchid"
                >
                  <span className="flex items-center gap-2 font-bold">
                    <Icon name="folder" className="h-6 w-6 text-brand-magenta" />
                    {topic.name || topic.id}
                  </span>
                  {topic.description && <span className="line-clamp-3 text-small">{topic.description}</span>}
                  <span className="mt-auto text-small text-neutral-muted">
                    {strings.home.topicItems(topic.knowledgeItemCount)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
